import React, { useCallback, useContext, useEffect, useState } from "react"
import { Switch, Route, RouteComponentProps, useHistory } from "react-router-dom"

import { useLocalization } from "@izettle/lingo-client"
import { AuthContext } from "@izettle/react-spa"

import { baseUrl } from "@/constants"
import { LoaContract, LoaFormSteps } from "@/models"
import { usePurposes } from "@/providers/purposes"
import { submitLoa } from "@/services"
import { ViewedPurposeEvent, RequestedLoaEvent, sendToTrackingService } from "@/tracking"

import { AuthorizerDetails } from "./authorizer-details"
import { EmployeeDetails } from "./employee-details"
import { isEmployeeValid, isAuthorizerValid, isSubmitDataValid } from "./helpers"
import { Overview } from "./overview"
import { Submit } from "./submit"

const validateSteps = (updatedContract: LoaContract, validSteps: LoaFormSteps) => ({
  ...validSteps,
  employee: isEmployeeValid(updatedContract),
  authorizer: isAuthorizerValid(updatedContract),
  submit: isSubmitDataValid(updatedContract),
})

export const LoaView: React.FC<RouteComponentProps> = ({ match }) => {
  const { text } = useLocalization()
  const { getToken } = useContext(AuthContext)
  const history = useHistory()
  const { checkValidPurpose } = usePurposes()

  const [contract, setContract] = useState<LoaContract>({
    email: "",
    employeeFirstName: "",
    employeeLastName: "",
    employeePosition: "",
    firstName: "",
    lastName: "",
    phoneNumber: text("LOA_PHONE_NUMBER_COUNTRY_CODE"),
    position: "",
  })
  const [validSteps, setValidSteps] = useState<LoaFormSteps>({
    employee: false,
    authorizer: false,
    submit: false,
  })
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [successfullySubmittedToBackend, setSuccessfullySubmittedToBackend] = useState(false)
  const [submitError, setSubmitError] = useState<boolean>(false)

  useEffect(() => {
    if (!successfullySubmittedToBackend) return;

    const timer = setTimeout(() => history.push(baseUrl), 2000);
    return () => clearTimeout(timer);
  }, [successfullySubmittedToBackend])

  const updateContract = useCallback(
    (field: keyof LoaContract, value: string) => {
      const updatedContract: LoaContract = { ...contract, [field]: value }
      setContract(updatedContract)
      setValidSteps(validateSteps(updatedContract, validSteps))
    },
    [contract],
  )

  const redirectNext = (url: string) => {
    history.push(url)
  }

  const submit = useCallback(async () => {
    setIsSubmitting(true)
    setSuccessfullySubmittedToBackend(false)

    try {
      await submitLoa(getToken, contract)
      setIsSubmitting(false)
      const payload = {
        email: contract.email !== "",
        sms: contract.phoneNumber !== "",
        error: false,
      }
      sendToTrackingService({ ...RequestedLoaEvent, payload })
      setSuccessfullySubmittedToBackend(true)
    } catch {
      setIsSubmitting(false)
      setSubmitError(true)
      const payload = {
        email: contract.email !== "",
        sms: contract.phoneNumber !== "",
        error: submitError,
      }
      sendToTrackingService({ ...RequestedLoaEvent, payload })
    }
  }, [contract])

  const checkIfPageIsValid = async () => {
    const isValid = await checkValidPurpose("letterOfAuthorization")
    if (!isValid) {
      history.replace(baseUrl)
      return
    }
    const payload = {
      purpose: "letterOfAuthorization",
    }
    sendToTrackingService({ ...ViewedPurposeEvent, payload })
  }

  useEffect(() => {
    void checkIfPageIsValid()
  }, [])

  return (
    <Switch>
      <Route
        exact
        path={`${match.path}`}
        render={() => (
          <Overview
            backUrl={baseUrl}
            nextUrl={`${baseUrl}/letterOfAuthorization/employee`}
            redirectNext={redirectNext}
          />
        )}
      />
      <Route
        path={`${match.path}/employee`}
        render={() => (
          <EmployeeDetails
            contract={contract}
            backUrl={`${baseUrl}/letterOfAuthorization`}
            nextUrl={`${baseUrl}/letterOfAuthorization/authorizer`}
            updateContract={updateContract}
            redirectNext={redirectNext}
          />
        )}
      />
      <Route
        path={`${match.path}/authorizer`}
        render={() => (
          <AuthorizerDetails
            contract={contract}
            backUrl={`${baseUrl}/letterOfAuthorization/employee`}
            nextUrl={`${baseUrl}/letterOfAuthorization/submit`}
            validSteps={validSteps}
            updateContract={updateContract}
            redirectNext={redirectNext}
          />
        )}
      />
      <Route
        path={`${match.path}/submit`}
        render={() => (
          <Submit
            contract={contract}
            backUrl={`${baseUrl}/letterOfAuthorization/authorizer`}
            validSteps={validSteps}
            isSubmitting={isSubmitting}
            successfullySubmittedToBackend={successfullySubmittedToBackend}
            submitError={submitError}
            updateContract={updateContract}
            submit={submit}
          />
        )}
      />
    </Switch>
  )
}
